
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../../../core/common/common.service';
import { Observable, of } from 'rxjs';
import {
    AgencyFullDetailResponse,
    AgencyNotificationSettingsRequest,
    AgencyNotificationSettingsResponse,
    AgencyReportResponse,
} from './api-response-shapes.types';

@Injectable({
    providedIn: 'root'
})
export class DataService {
    private apiUrl: string = environment.platformUrl;
    constructor(private http: HttpClient, private commonService: CommonService) { }

    private baseUrl = environment.api_url;
    private appId = environment.app_id;
    private progressUrl = environment.progress_api_url;
    private tenantId = environment.tenant_id;


    /**
     * Create applicant Details
     * @param submissionId Submission ID (request body)
     * @param txnId Transaction ID (request body)
     * @param body Applicant details object (request body)
     */
    public createApplicantDetails(body: any): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });

        return this.http.post(
            `${this.baseUrl}applicant-details`,
            body, {
            headers
        }
        );
    }

    /**
     * Get applicant details
     * @param submissionId Submission ID (query param)
     */
    public getApplicantDetails(submissionId: string, txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('submission_id', submissionId).set('txn_id', txnId);
        return this.http.get(`${this.baseUrl}applicant-details`, {
            headers,
            params
        });
    }

    /**
     * Update applicant details
     * @param submissionId Submission ID (query param)
     * @param body Applicant details object (request body)
     */
    public updateApplicantDetails(submissionId: string, body: any): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('submission_id', submissionId);
        return this.http.put(`${this.baseUrl}applicant-details`, body, {
            headers,
            params
        });
    }

    /**
     * Create property details
     * @param submissionId Submission ID (request body)
     * @param txnId Transaction ID (request body)
     * @param body Property details object (request body)
     */
    public createPropertyDetails(body: any): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });

        return this.http.post(`${this.baseUrl}property-details`, body, {
            headers
        });
    }

    /**
     * Get property details
     * @param submissionId Submission ID (query param)
     */
    public getPropertyDetails(submissionId: string, txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('submission_id', submissionId).set('txn_id', txnId);
        return this.http.get(`${this.baseUrl}property-details`, {
            headers,
            params
        });
    }

    /**
     * Create coverage preferences
     * @param submissionId Submission ID (request body)
     * @param txnId Transaction ID (request body)
     * @param body Coverage preferences object (request body)
     */
    public createCoveragePreferences(
        submissionId: string,
        txnId: string,
        body: any,
        quoteId?: string,
        submitForReview?: boolean,
    ): Observable<any> {

        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });

        const bodyWithSubmission = {
            ...body,
            submission_id: submissionId,
            ...(submitForReview && {
                action: 'submit_for_review'
            })
        };

        const bodyWithIds = {
            ...bodyWithSubmission,
            txn_id: txnId,
            quote_id: quoteId
        };

        return this.http.post(
            `${this.baseUrl}coverage-preferences`,
            bodyWithIds, {
            headers
        }
        );
    }

    /**
     * Get coverage preferences
     * @param submissionId Submission ID (query param)
     */
    public getCoveragePreferences(submissionId: string, txnId: string, quoteId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('submission_id', submissionId).set('txn_id', txnId).set('quote_id', quoteId);
        return this.http.get(`${this.baseUrl}coverage-preferences`, {
            headers,
            params
        });
    }

    /**
     * Create underwriting information
     * @param body Underwriting information object (request body)
     */
    public createUnderWriterInfo(body: any): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });

        return this.http.post(
            `${this.baseUrl}underwriting-information`,
            body, {
            headers
        }
        );
    }

    /**
     * Get underwriting information
     * @param submissionId Submission ID (query param)
     * @param txnId Transaction ID (query param)
     */
    public getUnderWriterInfo(txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('txn_id', txnId);
        return this.http.get(`${this.baseUrl}underwriting-information`, {
            headers,
            params
        });
    }

    /**
     * Create application progress
     * @param body Application progress object (request body)
     */
    public getApplicationProgress(txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'tenant-id': this.tenantId,
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('txn_id', txnId);
        return this.http.get(
            `${this.progressUrl}api/v1/progress-tracker`, {
            headers,
            params
        }
        );
    }

    public triggerRiskApi(txnId: string): Observable<any> {
    const headers = new HttpHeaders({
        'tenant-id': this.tenantId,
        'app-id': this.appId,
        'x-email-id': this.commonService.getEmail(),
        'Content-Type': 'application/json'
    });

    const body = {
        txn_id: txnId
    };

    return this.http.post(`${this.baseUrl}trigger-risk-api`, body, { headers });
}

 public getTxnIdBySubmission(submissionId: string, accessToken?: string): Observable<any> {
    const params = new HttpParams().set('submission_id', submissionId);
    const headers = accessToken
        ? new HttpHeaders({ Authorization: `Bearer ${accessToken}` })
        : undefined;

    return this.http.get(
        `${this.baseUrl}submission/transaction-identifiers`,
        { params, headers }
    );
}

    public getAgencyDetails(agencyName: string): Observable<any> {
        const headers = new HttpHeaders({
            'tenant-id': this.tenantId,
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('agency_name', agencyName);
        return this.http.get<any>(`${this.baseUrl}agency`, {
            headers,
            params
        });
    }

    public getAgentDetails(agencyId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('agency_id', agencyId).set('entity_type', 'AGENCY');
        return this.http.get<any>(`${this.baseUrl}user/agent-details`, {
            headers,
            params
        });
    }

    public getAgentDetailsBySubmissionId(submissionId: string, txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('submission_id', submissionId).set('txn_id', txnId);
        return this.http.get(`${this.baseUrl}agency-details`, {
            headers,
            params
        });
    }

    public createAdditionalInterests(body: any): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });
        return this.http.post(
            `${this.baseUrl}additional-interests`,
            body, {
            headers
        }
        );
    }

    /**
     * Get additional interests
     * @param submissionId Submission ID (query param)
     */
    public getAdditionalInterests(submissionId: string, txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('submission_id', submissionId).set('txn_id', txnId);
        return this.http.get(`${this.baseUrl}additional-interests`, {
            headers,
            params
        });
    }

    /**
     * Get transaction details
     * @param txnId transaction ID (query param)
     */
    public deleteSubmission(txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });

        return this.http.delete(
            `${this.baseUrl}dashboard/submission?txn_id=${txnId}`, {
            headers
        }
        );
    }

    public getQuoteDetails(submissionId: string, txnId: string, quoteId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('submission_id', submissionId).set('txn_id', txnId).set('quote_id', quoteId);

        return this.http.get(`${this.baseUrl}quote-summary`, {
            headers,
            params
        });
    }

    // public generateQuote(txnId: string): Observable<any> {    
    //   const body = {
    //     txn_id: txnId
    //   };

    //   return this.http.post(`${this.baseUrl}generate-quote`, body);
    // }    


    public generateQuote(txnId: string, action?: string): Observable<any> {
        const body: any = {
            txn_id: txnId
        };

        // only attach if provided
        if (action) {
            body.actions = action;
        }

        return this.http.post(`${this.baseUrl}generate-quote`, body);
    }
    public getQuotesByTxnId(txnId: string): Observable<any> {
        const headers = new HttpHeaders({
            'app-id': this.appId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json'
        });

        const params = new HttpParams().set('txn_id', txnId);

        return this.http.get(`${this.baseUrl}quotes`, {
            headers,
            params
        });
    }

    public copyAndEditQuote(txnId: string, quoteId: string): Observable<any> {
        const body = {
            txn_id: txnId,
            quote_id: quoteId
        };

        return this.http.put(`${this.baseUrl}copy-quote`, body);
    }

    public downloadQuote(quoteId: string): Observable<Blob> {

        const params = new HttpParams().set('quote_id', quoteId);

        return this.http.get(`${this.baseUrl}download/quote`, {
            params,
            responseType: 'blob'
        });
    }

    public getAgentDocuments(txnId: string): Observable<any> {
        const params = new HttpParams().set('txn_id', txnId);

        return this.http.get(`${this.baseUrl}submission/documents`, {
            params
        });
    }

    public downloadAgentDocument(
        txnId: string,
        documentId: string
    ): Observable<Blob> {

        const params = new HttpParams()
            .set('txn_id', txnId)
            .set('filename', documentId);

        return this.http.get(`${this.baseUrl}submission/document/download`, {
            params,
            responseType: 'blob'
        });
    }

    verifyOtp(payload: any, email: string, tenantId: string, appId: string): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'x-email-id': email,
            'tenant-id': tenantId,
            'app-id': appId
        });
        return this.http.post<any>(`${this.apiUrl}auth/v1/verify/otp`, payload, { headers });
    }

    resendOtp(email: string, tenantId: string, appId: string): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'x-email-id': email,
            'tenant-id': tenantId,
            'app-id': appId
        });
        return this.http.post<any>(`${this.apiUrl}auth/v1/generate/otp/`, {}, { headers });
    }

    public getAgentSummary(emailId: string): Observable<any> {
        const headers = new HttpHeaders({
            'tenant-id': this.tenantId,
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('email_id', emailId);
        return this.http.get<any>(`${this.baseUrl}agent-summary`, {
            headers,
            params
        });
    }
    public getAgencyInfo(params: { agency_npn?: string; agency_id?: string }): Observable<any> {
        const headers = new HttpHeaders({
            'tenant-id': this.tenantId,
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });

        let httpParams = new HttpParams();
        if (params.agency_npn) httpParams = httpParams.set('agency_npn', params.agency_npn);
        if (params.agency_id) httpParams = httpParams.set('agency_id', params.agency_id);

        return this.http.get<any>(`${this.baseUrl}agency`, { headers, params: httpParams });
    }

    checkEmailExists(email_id: string): Observable<any> {
        const headers = new HttpHeaders({
            'tenant-id': this.tenantId,
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });

        const params = new HttpParams().set('email_id', email_id);

        return this.http.get<{ valid: boolean }>(
            `${this.baseUrl}validate-user`,
            { headers, params }
        );
    }

    public getLookupValues(lookupCategory: string, lookupCode: string): Observable<any> {
        const headers = new HttpHeaders({
            'tenant-id': this.tenantId,
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        const params = new HttpParams().set('lookup_category', lookupCategory).set('lookup_code', lookupCode);
        return this.http.get(`${this.baseUrl}lookup-values`, {
            headers,
            params
        });
    }

    public getOverviewData(from?: string, to?: string): Observable<any> {
        const headers = new HttpHeaders({ 'app-id': this.appId, 'tenant-id': this.tenantId, 'Content-Type': 'application/json' });
        let params = new HttpParams();
        if (from) params = params.set('from_date', from);
        if (to) params = params.set('to_date', to);
        return this.http.get<any>(`${this.baseUrl}agency/overview`, { headers, params });
    }

    public getSubmissionOverview(from?: string, to?: string): Observable<any> {
        const headers = new HttpHeaders({ 'app-id': this.appId, 'tenant-id': this.tenantId, 'Content-Type': 'application/json' });
        let params = new HttpParams();
        if (from) params = params.set('from_date', from);
        if (to) params = params.set('to_date', to);
        return this.http.get<any>(`${this.baseUrl}submission/overview`, { headers, params });
    }

    public getProducerflowSyncOverview(from?: string, to?: string): Observable<any> {

        const headers = new HttpHeaders({
            'tenant-id': this.tenantId,
            'app-id': this.appId,
            'Content-Type': 'application/json'
        });
        let params = new HttpParams();
        if (from) params = params.set('from_date', from);
        if (to) params = params.set('to_date', to);
        return this.http.get<any>(`${this.baseUrl}producerflow/sync/overview`, { headers, params });
    }
    /**
     * Report mode — called for outer grids (exceptions, stage-conversion,
     * registration-lifecycle).  Requires UNDERWRITER role.
     *
     * @param fromDate  MM-DD-YYYY
     * @param toDate    MM-DD-YYYY
     * @param userRoles comma-separated role string sent as x-user-roles header
     */
    getAgencyReport(
        fromDate: string,
        toDate: string,
        userRoles: string
    ): Observable<AgencyReportResponse> {
        const params = new HttpParams()
            .set('from_date', fromDate)
            .set('to_date', toDate);
        const headers = new HttpHeaders({ 'x-user-roles': userRoles, 'app-id': this.appId, 'tenant-id': this.tenantId });
        return this.http.get<AgencyReportResponse>(`${this.baseUrl}agency`, { params, headers });
    }

    /**
     * Full-details mode — called when a row toggle is opened.
     * Requires UNDERWRITER role.
     *
     * @param agencyId  The agency_id from the outer-grid row
     * @param userRoles comma-separated role string sent as x-user-roles header
     */
    getAgencyFullDetail(
        agencyId: string,
        userRoles: string
    ): Observable<AgencyFullDetailResponse> {
        const params = new HttpParams()
            .set('agency_id', agencyId)
            .set('full_details', 'true');
        const headers = new HttpHeaders({ 'x-user-roles': userRoles, 'app-id': this.appId, 'tenant-id': this.tenantId });
        return this.http.get<AgencyFullDetailResponse>(`${this.baseUrl}agency`, { params, headers });
    }

    /**
     * Notification settings for an agency.
     * Requires UNDERWRITER or UW_ASSISTANT role.
     *
     * @param agencyId  The agency whose settings are returned
     * @param userRoles comma-separated role string sent as x-user-roles header
     */
    getAgencyNotificationSettings(
        agencyId: string,
        userRoles: string
    ): Observable<AgencyNotificationSettingsResponse> {
        const params = new HttpParams().set('agency_id', agencyId);
        const headers = new HttpHeaders({ 'x-user-roles': userRoles, 'app-id': this.appId, 'tenant-id': this.tenantId });
        return this.http.get<AgencyNotificationSettingsResponse>(`${this.baseUrl}agency/notification-settings`, { params, headers });
    }

    /**
     * Replaces an agency's notification settings.
     * Requires UNDERWRITER role.
     *
     * @param body      Full settings payload (cc_emails and bcc_emails always sent)
     * @param userRoles comma-separated role string sent as x-user-roles header
     */
    updateAgencyNotificationSettings(
        body: AgencyNotificationSettingsRequest,
        userRoles: string
    ): Observable<AgencyNotificationSettingsResponse> {
        const headers = new HttpHeaders({
            'x-user-roles': userRoles,
            'app-id': this.appId,
            'tenant-id': this.tenantId,
            'x-email-id': this.commonService.getEmail(),
            'Content-Type': 'application/json',
        });
        return this.http.put<AgencyNotificationSettingsResponse>(`${this.baseUrl}agency/notification-settings`, body, { headers });
    }

}